import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EyeIcon, EyeSlashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import AppleSignInButton from '../AppleSignInButton';
import { AppleSignInCancelled } from '../../services/appleSignIn';
import { PasswordValidator, validatePassword, type PasswordValidation } from '../ui/PasswordValidator';
import { events } from '../../utils/analytics';

interface SignUpModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToLogin: () => void;
  /** Zie LoginModal: in de native app is er niets om naar terug te keren. */
  dismissible?: boolean;
}

export const SignUpModal: React.FC<SignUpModalProps> = ({ 
  isOpen, 
  onClose, 
  onSwitchToLogin ,
  dismissible = true
}) => {
  const { t } = useTranslation('auth');
  const { signup, loginWithApple: appleLogin } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPasswordValidation, setShowPasswordValidation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Password validation using the new validator
  const passwordValidation: PasswordValidation = validatePassword(password);
  const passwordsMatch = password === confirmPassword;

  /**
   * Registreren met Apple is dezelfde aanroep als inloggen: kent Apple het adres nog niet,
   * dan maakt de backend het account aan. Vandaar dat hier geen aparte registratieflow staat.
   */
  const handleAppleSignIn = async () => {
    setError('');
    setAppleLoading(true);
    try {
      const ok = await appleLogin();
      if (ok) {
        onClose();
      }
    } catch (err) {
      if (err instanceof AppleSignInCancelled) {
        return;
      }
      console.error('Registreren met Apple mislukt:', err);
      setError(t('apple.failed'));
    } finally {
      setAppleLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!passwordValidation.isValid) {
      setError(t('signup.passwordRequirementsNotMet'));
      return;
    }
    
    if (!passwordsMatch) {
      setError(t('signup.passwordsMismatch'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const success = await signup({
        firstName,
        lastName,
        email,
        password
      });
      
      if (success) {
        events.accountAangemaakt();
        setSuccess(t('signup.created'));
        toast.success(t('signup.createdToast'));
        setTimeout(() => {
          // Reset form fields
          setFirstName('');
          setLastName('');
          setEmail('');
          setPassword('');
          setConfirmPassword('');
          setShowPasswordValidation(false);
          setSuccess('');
          // Switch to login modal
          onSwitchToLogin();
        }, 3000);
      } else {
        setError(t('signup.createFailed'));
        toast.error(t('signup.createFailed'));
      }
    } catch (err) {
      setError(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFirstName('');
      setLastName('');
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setError('');
      setSuccess('');
      setShowPasswordValidation(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-sluier bg-opacity-75 transition-opacity"
          onClick={handleClose}
        ></div>

        {/* Modal panel */}
        <div className="inline-block w-full align-bottom bg-white dark:ring-1 dark:ring-white/15 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="w-full">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <div>
                        <h3 className="text-lg font-medium text-hockey-brown">
                          {t('signup.title')}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {t('signup.subtitle')}
                        </p>
                      </div>
                    </div>
                    {dismissible && (
                      <button
                        type="button"
                        onClick={handleClose}
                        disabled={loading}
                        className="text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    )}
                  </div>

                  {/* Success message */}
                  {success && (
                    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                      <p className="text-sm text-green-700">{success}</p>
                    </div>
                  )}

                  {/* Error message */}
                  {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}

                  {/* Form fields */}
                  <div className="space-y-4">
                    {/* Name fields */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                          {t('fields.firstName')}
                        </label>
                        <input
                          type="text"
                          id="firstName"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          required
                          disabled={loading}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-hockey-blue focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                          placeholder={t('fields.firstNamePlaceholder')}
                        />
                      </div>
                      <div>
                        <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                          {t('fields.lastName')}
                        </label>
                        <input
                          type="text"
                          id="lastName"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          required
                          disabled={loading}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-hockey-blue focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                          placeholder={t('fields.lastNamePlaceholder')}
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        {t('fields.emailLong')}
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={loading}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-hockey-blue focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder={t('fields.emailLongPlaceholder')}
                      />
                    </div>


                    {/* Password */}
                    <div>
                      <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                        {t('fields.password')}
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          id="password"
                          value={password}
                          onChange={(e) => {
                            setPassword(e.target.value);
                            if (e.target.value.length > 0) {
                              setShowPasswordValidation(true);
                            }
                          }}
                          onFocus={() => {
                            if (password.length > 0) {
                              setShowPasswordValidation(true);
                            }
                          }}
                          required
                          disabled={loading}
                          className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-hockey-blue focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed ${
                            password.length > 0 
                              ? passwordValidation.isValid 
                                ? 'border-green-300' 
                                : 'border-red-300'
                              : 'border-gray-300'
                          }`}
                          placeholder={t('fields.passwordPlaceholder')}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          disabled={loading}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {showPassword ? (
                            <EyeSlashIcon className="h-5 w-5" />
                          ) : (
                            <EyeIcon className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                      
                      {/* Real-time password validation feedback */}
                      <PasswordValidator 
                        password={password}
                        showValidation={showPasswordValidation}
                        validation={passwordValidation}
                      />
                    </div>

                    {/* Confirm Password */}
                    <div>
                      <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                        {t('fields.passwordRepeat')}
                      </label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          id="confirmPassword"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                          disabled={loading}
                          className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-hockey-blue focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed ${
                            confirmPassword.length > 0 
                              ? passwordsMatch 
                                ? 'border-green-300' 
                                : 'border-red-300'
                              : 'border-gray-300'
                          }`}
                          placeholder={t('fields.passwordRepeatPlaceholder')}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          disabled={loading}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {showConfirmPassword ? (
                            <EyeSlashIcon className="h-5 w-5" />
                          ) : (
                            <EyeIcon className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                      {confirmPassword && (
                        <div className={`mt-2 text-sm ${passwordsMatch ? 'text-green-600' : 'text-red-600'}`}>
                          {passwordsMatch ? (
                            <div className="flex items-center space-x-1">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              <span>{t('signup.passwordsMatch')}</span>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-1">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                              <span>{t('signup.passwordsMismatch')}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 dark:bg-gray-100 px-4 py-3 sm:px-6">
              <div className="flex flex-col space-y-3">
                <button
                  type="submit"
                  disabled={loading || !firstName || !lastName || !email || !passwordValidation.isValid || !passwordsMatch}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-hockey-blue text-base font-medium text-white hover:bg-hockey-blue-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hockey-blue disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {t('signup.submitting')}
                    </div>
                  ) : (
                    t('signup.submit')
                  )}
                </button>

                <div className="flex items-center">
                  <div className="flex-grow border-t border-gray-200" />
                  <span className="mx-3 text-xs uppercase tracking-wide text-gray-400">{t('apple.or')}</span>
                  <div className="flex-grow border-t border-gray-200" />
                </div>

                <AppleSignInButton onClick={handleAppleSignIn} disabled={loading} loading={appleLoading} />
                
                <div className="flex justify-between items-center">
                  
                  <button
                    type="button"
                    onClick={onSwitchToLogin}
                    disabled={loading}
                    className="text-sm text-hockey-blue hover:text-hockey-brown disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('signup.haveAccount')}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};